import { useState, useEffect, Fragment  } from 'react';
import './style/CustomCalendar.css'
import { DataTable } from 'mantine-datatable'
import { Link, useNavigate } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react';
import IconX from '../../component/icon/IconX';
import api from '../../api/api';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedCourse, setSelectedAttendance, setCheckInImage, setCurrentDate } from '../../store/slice/AttendanceSlice';
import Loader from '../../component/layout/Loader';
import { t } from 'i18next';
import IconCamera from '../../component/icon/IconCamera';
import IconHorizontalDots from '../../component/icon/IconHorizontalDots';
import CameraComponent from '../../component/CameraComponent';
import IconClipboardCheck from '../../component/icon/IconClipboardCheck';
import CalendarView from './component/CalendarView';
import useAppSelector from '../../hooks/useAppSelector';
import { toast } from 'react-toastify';


type Props = {}

const PAGE_SIZES = [10, 20, 30, 50, 100]
const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

const ScheduleView = (props: Props) => {
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState([])
    const [initialRecords, setInitialRecords] = useState(items)
    const [records, setRecords] = useState(initialRecords)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { currentDate } = useSelector((state: any) => state.attendance)

    // Camera related state
    const [showCamera, setShowCamera] = useState(false)

    // Branch selection state
    const [selectedBranch, setSelectedBranch] = useState("")
    const [branches, setBranches] = useState<Array<{id: number, name: string}>>([])
    const [branchesLoading, setBranchesLoading] = useState(false)

    // Check-in related state
    const [isCheckInBefore, setIsCheckInBefore] = useState(false)
    const [checkInData, setCheckInData] = useState<{
        branch_name: string,
        check_in_time: Date | null
    } | null>(null)
    const [elapsedTime, setElapsedTime] = useState("")

    // Check-out related state
    const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false)
    const [checkoutData, setCheckoutData] = useState<{
        branch_name: string,
        check_in_time: string,
        check_out_time: string
    } | null>(null)

    // Course modal state
    const [showCourseModal, setShowCourseModal] = useState(false)
    const [courses, setCourses] = useState<any>([])
    const [selectedDate, setSelectedDate] = useState("")

    const {scheduleList} = useAppSelector((state) => state.schedule);

    // Helper functions
    const openCourse = (courses: any = null, date: string) => {
        setCourses(courses)
        setSelectedDate(date)
        setShowCourseModal(true)
    }

    const handleDayClick = (date: Date) => {
        setCourses(scheduleList.find(
            (item: any) => item.date === date.toLocaleDateString("en-CA")
        )?.courses)
        setSelectedDate(date.toLocaleDateString("en-CA"));
        setShowCourseModal(true);
    };

    // const getAttendanceList = (dateInput: Date) => {
    //     const date = new Date(dateInput);
    //     console.log("Fetching attendance for:", date.toISOString().split("T")[0])
    //     const formattedDate = date.toISOString().split("T")[0]
    //     setLoading(true)
    //     api.get(`schedule/${formattedDate}`).then(res => {
    //         setLoading(false)
    //         console.log("res schedule", res.data.data)
            
    //         // Sort courses by start_time in ascending order
    //         const sortedData = res.data.data.map((entry: any) => ({
    //             ...entry,
    //             courses: entry.courses.sort((a: any, b: any) => {
    //                 if (!a.start_time) return 1
    //                 if (!b.start_time) return -1
    //                 return a.start_time.localeCompare(b.start_time)
    //             })
    //         }))

    //         setInitialRecords(sortedData)
    //     }).catch(err => {
    //         setLoading(false)
    //         console.log("err", err)
    //     })
    // }

    const getCurrentClockStatus = () => {
        setLoading(true)
        api.get('coach/clock/latest').then(res => {
            setLoading(false)
            const responseData = res.data.data || res.data
            
            if (responseData.is_check_in_before) {
                setIsCheckInBefore(true)
                setCheckInData({
                    branch_name: responseData.branch_name || "",
                    check_in_time: new Date(responseData.last_clock_in)
                })
                dispatch(setCheckInImage("checked_in"))
            } else {
                setIsCheckInBefore(false)
                setCheckInData(null)
                dispatch(setCheckInImage(""))
            }
        }).catch(err => {
            setLoading(false)
            console.log("Error fetching clock status:", err)
            setIsCheckInBefore(false)
            setCheckInData(null)
            dispatch(setCheckInImage(""))
        })
    }

    const getBranches = () => {
        setBranchesLoading(true)
        api.get('branch').then(res => {
            setBranchesLoading(false)
            console.log("Branches response:", res.data)
            if (res.data.result && res.data.result.data) {
                setBranches(res.data.result.data)
            }
        }).catch(err => {
            setBranchesLoading(false)
            console.log("Error fetching branches:", err)
        })
    }

    const calculateElapsedTimeFromCheckout = (checkInTime: string, checkOutTime: string) => {
        const checkIn = new Date(checkInTime)
        const checkOut = new Date(checkOutTime)
        const diff = checkOut.getTime() - checkIn.getTime()
        if (diff > 0) {
            const hours = Math.floor(diff / (1000 * 60 * 60))
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
            const seconds = Math.floor((diff % (1000 * 60)) / 1000)
            
            return `${hours}h ${minutes}m ${seconds}s`
        }
        return "0h 0m 0s"
    }

    const changeWeek = (direction: "prev" | "next") => {
        const newDate = new Date(currentDate)
        newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7))
        dispatch(setCurrentDate(newDate))
    }

    // Pagination and records management
    useEffect(() => {
        setPage(1)
    }, [pageSize])

    useEffect(() => {
        const from = (page - 1) * pageSize
        const to = from + pageSize
        setRecords([...initialRecords.slice(from, to)])
    }, [page, pageSize, initialRecords])

    // Initial data loading on component mount
    useEffect(() => {
        getCurrentClockStatus()
        getBranches()
        // getAttendanceList(currentDate)
    }, [])

    // Fetch attendance when date changes (but not on initial mount)
    // useEffect(() => {
    //     if (currentDate) {
    //         getAttendanceList(currentDate)
    //     }
    // }, [currentDate])

    // Elapsed time calculation timer
    useEffect(() => {
        if (checkInData?.check_in_time && isCheckInBefore) {
            const timer = setInterval(() => {
                const now = new Date()
                const diff = now.getTime() - checkInData.check_in_time!.getTime()
                
                if (diff > 0) {
                    const hours = Math.floor(diff / (1000 * 60 * 60))
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
                    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
                    
                    setElapsedTime(`${hours}h ${minutes}m ${seconds}s`)
                } else {
                    setElapsedTime("0h 0m 0s")
                }
            }, 1000)

            return () => clearInterval(timer)
        } else {
            setElapsedTime("")
        }
    }, [checkInData?.check_in_time, isCheckInBefore])

    return (
        <div>
            {loading && (<Loader/>)}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <h2 className="text-xl">{t('schedule.schedules')}</h2>
            </div>
            {isCheckInBefore ? (
            <div className="my-2 rounded-2xl border border-orange-300 p-4 bg-white shadow-sm max-w-md mx-auto">
                <h2 className="text-center font-semibold mb-4">{t('schedule.checked_in')}</h2>

                <div className="flex items-center gap-3 w-full">
                    <input 
                        type="text"
                        value={checkInData?.branch_name || ""}
                        disabled
                        className="bg-orange-100 text-black rounded-full px-4 py-2 flex-1 min-w-0 outline-none cursor-not-allowed"
                    />
                    <button className="bg-orange-500 p-4 rounded-2xl flex-shrink-0" onClick={() => setShowCamera(true)}>
                        <IconCamera className="w-6 h-6 text-white" />
                    </button>
                </div>

                {/* Add time display */}
                <div className="mt-4 grid grid-cols-12 items-center">
                    <div className="col-span-11 text-center">
                        <div className="text-3xl font-bold">{elapsedTime}</div>
                        <div className="text-gray-500 mt-1">
                            {checkInData?.check_in_time?.toLocaleString('en-US', {
                                hour: 'numeric',
                                minute: 'numeric',
                                hour12: true,
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            })}
                        </div>
                    </div>
                    <Link to="/checkin/history" className="col-span-1 bg-gray-100 p-2 rounded-full flex justify-center">
                        <IconHorizontalDots className="w-5 h-5 text-black" />
                    </Link>
                </div>
            </div>
            ):(
            <div className="my-2 rounded-2xl border border-green-300 p-4 bg-white shadow-sm max-w-md mx-auto">
                <h2 className="text-center font-semibold mb-4">{t('schedule.check_in')}</h2>

                <div className="flex items-center gap-3 w-full">
                    {/* Dropdown */}
                    <select 
                        className="bg-green-100 text-black rounded-full px-4 py-2 flex-1 min-w-0 outline-none"
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                        disabled={branchesLoading}
                    >
                        <option value="">{branchesLoading ? 'Loading branches...' : t('schedule.select_branch')}</option>
                        {branches.map((branch) => (
                            <option key={branch.id} value={branch.name}>
                                {branch.name}
                            </option>
                        ))}
                    </select>

                    {/* Camera Button */}
                    <button 
                        className={`p-4 rounded-2xl flex-shrink-0 ${
                            selectedBranch 
                                ? "bg-emerald-500 hover:bg-emerald-600" 
                                : "bg-gray-300 cursor-not-allowed"
                        }`}
                        onClick={() => {
                            if (selectedBranch) {
                                setShowCamera(true)
                            }
                        }}
                        disabled={!selectedBranch || branchesLoading}
                    >
                        <IconCamera className={`w-6 h-6 ${
                            selectedBranch ? "text-white" : "text-gray-500"
                        }`} />
                    </button>
                </div>

                {/* 3 Dot Menu */}
                <div className="flex justify-end mt-4">
                    <Link to="/checkin/history" className="bg-gray-100 p-2 rounded-full">
                        <IconHorizontalDots className="w-5 h-5 text-black" />
                    </Link>
                </div>
            </div>
            )}

            {/* <div className="mt-5 panel p-0 border-0 overflow-hidden">
                <div className="table-responsive">
                    <DataTable
                        className="whitespace-nowrap table-hover invoice-table"
                        records={records}
                        columns={[
                            {
                                accessor: 'date',
                                title: t("date"),
                                render: ({ date, courses }) => (
                                    <div onClick={() => openCourse(courses, date)}>
                                        <div className="text-black hover:underline font-semibold">{`${date} (${days[new Date(date).getDay()]})`}</div>
                                    </div>
                                ),
                            },
                            {
                                accessor: 'courses',
                                title: t("courses"),
                                titleClassName:"No of Course",
                                render: ({ date, courses }:any) => (
                                    <div>
                                        <div className="text-black hover:underline font-semibold">{`${courses.length}`}</div>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>


            <div className="relative inline-flex align-middle float-end mt-3">
                <button type="button" className="btn btn-primary ltr:rounded-r-none rtl:rounded-l-none" onClick={() => changeWeek("prev")}>
                    {"<"}
                </button>
                <button type="button" className="btn btn-primary ltr:rounded-l-none rtl:rounded-r-none" onClick={() => changeWeek("next")}>
                    {">"}
                </button>
            </div> */}
            <div className="flex flex-col h-screen">
            <CalendarView onDayClick={handleDayClick} />
            </div>


            <Transition appear show={showCourseModal} as={Fragment} static >
                <Dialog as="div" open={showCourseModal} onClose={() => {
                    setShowCourseModal(false)
                    // setCameraFrontOrBack("environment") // reset back camera orientation
                }} className="relative z-[51]">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-[black]/60" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="panel border-0 p-0 rounded-lg overflow-hidden w-full max-w-5xl text-black dark:text-white-dark">
                                    <button type="button" onClick={() => setShowCourseModal(false)} className="absolute top-4 ltr:right-4 rtl:left-4 text-gray-400 hover:text-gray-800 dark:hover:text-gray-600 outline-none" >
                                        <IconX />
                                    </button>
                                    <div className="text-lg font-medium bg-[#fbfbfb] dark:bg-[#121c2c] ltr:pl-5 rtl:pr-5 py-3 ltr:pr-[50px] rtl:pl-[50px]">
                                        {selectedDate}
                                    </div>
                                    <div className="p-5 table-responsive">
                                        <table className="table-striped">
                                            <thead>
                                                <tr>
                                                    <th style={{ minWidth: "100px" }}></th>
                                                    <th style={{ minWidth: "200px" }}>{t('branch')}</th>
                                                    <th style={{ minWidth: "200px" }}>{t('venue')} & {t('time')}</th>
                                                    <th className="!text-center">{t('schedule.take_attendance')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {courses.map((course:any, index: number) => {
                                                const isCanceled = course.status?.toLowerCase() === "canceled";
                                                return (
                                                    <tr key={index} className={isCanceled ? "bg-gray-300 text-gray-500" : ""}>
                                                        <td>
                                                            {course.images && course.images.length > 0 ? (
                                                                <img src={course.images[0]} className="rounded-full object-cover" style={{ width: "50px", height: "50px" }} />
                                                            ):(
                                                                <img src={`/assets/images/koku-logo.png`} className="rounded-full object-cover" style={{ width: "50px", height: "50px" }} />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className='font-semibold'>{course.name}</div>
                                                            <div>{course.branch_name}</div>
                                                        </td>
                                                        <td>
                                                            {course.type=="course" && (<div>{course.classroom}</div>)}
                                                            {course.type=="event" && (<div>{course.location}</div>)}
                                                            {course.type=="course" && (<div>{course.start_time} - {course.end_time}</div>)}
                                                        </td>
                                                        <td>
                                                            <div className="flex gap-4 items-center justify-center">
                                                                <div  onClick={()=>{ 
                                                                    if(!isCanceled){
                                                                    dispatch(setSelectedCourse({
                                                                        name: course.name,
                                                                        id: course.course_slot_id
                                                                    }))
                                                                    dispatch(setSelectedAttendance({
                                                                        branch_name: course.branch_name,
                                                                    }))
                                                                    // getCameraStream()
                                                                    navigate(`/schedule/${selectedDate}/${course.class_code}`)
                                                                } else {
                                                                    toast.error("This class is canceled.");
                                                                }
                                                                }} className="btn btn-sm btn-outline-primary">
                                                                    {'>'}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>


            <Transition appear show={showCamera} as={Fragment} static >
                <Dialog as="div" open={showCamera} onClose={() => {
                    setShowCamera(false)
                }} className="relative z-[51]">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-[black]/60" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8 md:px-16 md:py-16">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="panel border-0 p-0 rounded-lg overflow-hidden w-full max-w-5xl text-black dark:text-white-dark">
                                    <div className="flex justify-between px-6 pt-4">
                                        <button type="button" onClick={() => {
                                            setShowCamera(false)
                                        }} className="col-span-2 ltr:right-4 rtl:left-4 text-gray-400 hover:text-gray-800 dark:hover:text-gray-600 outline-none" >
                                            <IconX />
                                        </button>
                                    </div>
                                    <div className="mt-4">
                                        <CameraComponent
                                            onCapture={(imageSrc) => {
                                                console.log(imageSrc)
                                                setShowCamera(false)
                                                if (!isCheckInBefore) {
                                                    // Check in logic
                                                    if (selectedBranch) {
                                                        // Find the selected branch ID
                                                        const selectedBranchObj = branches.find(branch => branch.name === selectedBranch)
                                                        const branchId = selectedBranchObj ? selectedBranchObj.id : 0
                                                        
                                                        setLoading(true)
                                                        // Call check-in API
                                                        api.post('coach/clock/in', {
                                                            branch_id: branchId,
                                                            clock_in_pic: imageSrc
                                                        }).then(res => {
                                                            console.log("Check-in response:", res.data)
                                                            setIsCheckInBefore(true)
                                                            setCheckInData({
                                                                branch_name: selectedBranch,
                                                                check_in_time: new Date()
                                                            })
                                                            dispatch(setCheckInImage(imageSrc))
                                                        }).catch(err => {
                                                            console.log("Check-in error:", err)
                                                            alert("Failed to check in. Please try again.")
                                                        }).finally(() => {
                                                            setLoading(false)

                                                        })
                                                    } else {
                                                        alert("Please select a branch before checking in")
                                                        setShowCamera(true) // Keep camera open
                                                        return
                                                    }
                                                } else {
                                                    
                                                    setLoading(true)
                                                    // Check out logic
                                                    api.post('coach/clock/out', {
                                                        clock_out_pic: imageSrc
                                                    }).then(res => {
                                                        console.log("Check-out response:", res.data)
                                                        const responseData = res.data.data
                                                        setCheckoutData(responseData)
                                                        setIsCheckInBefore(false)
                                                        setCheckInData(null)
                                                        dispatch(setCheckInImage(""))
                                                        setShowCheckoutSuccess(true)
                                                    }).catch(err => {
                                                        console.log("Check-out error:", err)
                                                        alert(t("schedule.checkout_failed"))
                                                    }).finally(() => {
                                                        setLoading(false)
                                                    })
                                                }
                                            }}
                                        />
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Successfully checked out modal */}
            <Transition appear show={showCheckoutSuccess} as={Fragment} static>
                <Dialog as="div" open={showCheckoutSuccess} onClose={() => setShowCheckoutSuccess(false)} className="relative z-[51]">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-[black]/60" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="panel border-0 rounded-lg overflow-hidden w-full max-w-md text-black dark:text-white-dark bg-white">

                                    <div className="my-2 rounded-2xl bg-white shadow-sm max-w-md mx-auto">
                                        <h2 className="text-center font-semibold mb-4">{t('schedule.checked_out')}!</h2>

                                        <div className="flex items-center justify-between gap-3">
                                            <input 
                                                type="text"
                                                value={checkoutData?.branch_name || checkInData?.branch_name || ""}
                                                disabled
                                                className="bg-[#c7edfd] text-black rounded-full px-4 py-2 flex-1 outline-none cursor-not-allowed"
                                            />
                                            <div className="bg-[#5ac8f9] p-4 rounded-2xl">
                                                <IconClipboardCheck className="w-6 h-6 text-white" />
                                            </div>
                                        </div>

                                        <div className="mt-4 grid grid-cols-12 items-center">
                                            <div className="col-span-10 text-center">
                                                <div className="text-3xl font-bold">
                                                    {checkoutData 
                                                        ? calculateElapsedTimeFromCheckout(checkoutData.check_in_time, checkoutData.check_out_time)
                                                        : elapsedTime
                                                    }
                                                </div>
                                                <div className="text-gray-500 mt-1">
                                                    {checkoutData 
                                                        ? `${new Date(checkoutData.check_in_time).toLocaleString('en-US', {
                                                            hour: 'numeric',
                                                            minute: 'numeric',
                                                            hour12: true,
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })} - ${new Date(checkoutData.check_out_time).toLocaleString('en-US', {
                                                            hour: 'numeric',
                                                            minute: 'numeric',
                                                            hour12: true
                                                        })}`
                                                        : checkInData?.check_in_time?.toLocaleString('en-US', {
                                                            hour: 'numeric',
                                                            minute: 'numeric',
                                                            hour12: true,
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })
                                                    }
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => {
                                                    setShowCheckoutSuccess(false)
                                                    setCheckoutData(null)
                                                }}
                                                className="col-span-2 btn btn-outline-primary rounded-full px-6"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    )
}

export default ScheduleView